import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/Icon/Icon";

interface IdentifierGroupProps {
  kind: "CAS" | "FEMA" | "EC";
  values: string[];
  copied: string | null;
  onCopy: (value: string, key: string) => void;
}

export function IdentifierGroup({ kind, values, copied, onCopy }: IdentifierGroupProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number; minWidth: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const closeT = useRef<ReturnType<typeof setTimeout> | null>(null);

  const vals = values || [];
  const extra = vals.length - 2;
  const truncated = extra > 0;

  const show = () => {
    if (!truncated) return;
    if (closeT.current != null) clearTimeout(closeT.current);
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPos({ left: r.left, top: r.top, minWidth: Math.max(r.width, 152) });
    }
    setOpen(true);
  };
  const hide = () => {
    closeT.current = setTimeout(() => setOpen(false), 130);
  };

  if (vals.length === 0) return <span style={{ fontSize: 10.5, color: "var(--ink-mute)" }}>—</span>;

  const Line = ({ value, reveal }: { value: string; reveal?: boolean }) => {
    const isCopied = copied === `${kind}:${value}`;
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onCopy(value, `${kind}:${value}`); }}
        title={`Copy ${kind} ${value}`}
        className={reveal ? "sl-popline" : "sl-idmono"}
        style={{
          display: "flex", alignItems: "center", gap: 5, width: "100%",
          padding: "1px 6px 1px 4px", background: "transparent", border: "none",
          borderRadius: 4, cursor: "pointer", textAlign: "left",
        }}
      >
        <span
          className="mono"
          style={{ fontSize: 11, color: "var(--ink-2)", letterSpacing: 0, whiteSpace: "nowrap", flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}
        >{value}</span>
        <span
          className={reveal ? undefined : "sl-copyglyph"}
          style={{ display: "inline-flex", color: isCopied ? "var(--brand-green)" : "var(--ink-mute)", opacity: reveal ? 0.55 : (isCopied ? 1 : 0), transition: "opacity 120ms", flexShrink: 0 }}
        >
          <Icon name={isCopied ? "check" : "copy"} size={9} />
        </span>
      </button>
    );
  };

  return (
    <div ref={ref} onMouseEnter={show} onMouseLeave={hide} style={{ minWidth: 0 }}>
      <div className="vstack" style={{ gap: 1, minWidth: 0 }}>
        <Line value={vals[0]} />
        {vals.length >= 2 && (
          <div className="hstack" style={{ gap: 4, minWidth: 0 }}>
            <span style={{ flex: 1, minWidth: 0, display: "flex" }}><Line value={vals[1]} /></span>
            {truncated && (
              <span style={{
                flexShrink: 0, fontSize: 9.5, fontWeight: 600, letterSpacing: 0, color: "var(--ink-4)",
                padding: "0 5px", borderRadius: 999, background: "var(--bg-sunk)", border: "1px solid var(--line)", cursor: "default",
              }}>+{extra}</span>
            )}
          </div>
        )}
      </div>
      {open && truncated && pos && createPortal(
        <div
          onMouseEnter={show}
          onMouseLeave={hide}
          className="fade-in"
          style={{
            position: "fixed", left: pos.left, top: pos.top, zIndex: 120,
            minWidth: pos.minWidth, maxWidth: 300, background: "var(--panel)", borderRadius: 6,
            boxShadow: "0 0 0 1px var(--line-strong), var(--shadow-3)", padding: 0,
          }}
        >
          <div className="vstack" style={{ gap: 1, maxHeight: 264, overflow: "auto", padding: "1px 0" }}>
            {vals.map((v) => <Line key={v} value={v} reveal />)}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
